import type { FacebookPage } from '../types';

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '@valwin/facto/components';

import { storeKey } from '../api';
import { getLoginStatus, login } from '../auth';
import { facebookQueries } from './queries';

export function useConnectFacebook(pharmacyId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Incremented on each cancellation to force a remount of the Switch component,
  // resetting Ark UI's internal state machine that gets stuck after the FB popup closes
  const cancelCount = useRef(0);

  const mutation = useMutation({
    mutationFn: async (): Promise<FacebookPage[]> => {
      // 1. Check if already configured (pages in cache)
      const cachedPages = queryClient.getQueryData(
        facebookQueries.pages(pharmacyId).queryKey
      );

      if (cachedPages && cachedPages.length > 0) {
        return cachedPages;
      }

      // 2. Check FB login status
      let status = await getLoginStatus();

      // 3. If not connected, open FB login popup
      if (status.status !== 'connected') {
        status = await login();
      }

      // 4. If still not connected, user cancelled — return unchanged state (no-op)
      if (status.status !== 'connected' || !status.authResponse?.accessToken) {
        cancelCount.current += 1;

        return cachedPages ?? [];
      }

      // 5. Send access token to backend, get pages back
      return storeKey(pharmacyId, status.authResponse.accessToken);
    },
    onSuccess: (pages) => {
      queryClient.setQueryData(
        facebookQueries.pages(pharmacyId).queryKey,
        pages
      );

      if (pages.length > 0) {
        toast.success(t('toast accountConnected'), {
          description: t('toast facebookAccountConnected description'),
        });
      }
    },
    onError: () => {
      toast.error(t('toast error'), {
        description: t('toast facebookAccountConnecting error description'),
      });
    },
  });

  return { ...mutation, cancelCount };
}
